/**
 * Cuộn preview HTML (shadow DOM) về giữa viewport.
 *
 * Nguồn gọi hợp lệ:
 * - Navigate (L1) → `scheduleNavigatePreviewScroll`
 * - Setting flash → `scrollPreviewHtmlElementIntoView` (previewSettingFlash)
 *
 * Không gọi khi click `[data-comp]` từ preview — user đã nhìn đúng vùng đó.
 */

const SCROLL_OPTS: ScrollIntoViewOptions = {
  behavior: 'smooth',
  block: 'center',
  inline: 'nearest',
}

/** Retry ngắn khi HTML preview chưa mount / chưa hydrate shadow. */
const NAVIGATE_SCROLL_RETRY_MS = 50
const NAVIGATE_SCROLL_MAX_ATTEMPTS = 12

let navigateScrollGeneration = 0
const navigateScrollTimers = new Set<number>()

/** Chặn scroll preview HTML tạm thời (vd. sau click data-comp → mở sidebar). */
let suppressPreviewHtmlScrollUntil = 0

/**
 * Preview click / deep-link sidebar: không để Navigate retry hay setting-flash
 * kéo preview về center trong lúc user vừa click đúng vùng đó.
 */
export function suppressPreviewHtmlScrollBriefly(ms = 1200): void {
  suppressPreviewHtmlScrollUntil = Date.now() + Math.max(0, ms)
  cancelScheduledNavigatePreviewScroll()
}

function isPreviewHtmlScrollSuppressed(): boolean {
  return Date.now() < suppressPreviewHtmlScrollUntil
}

/** Từ node lên body, băng qua shadow host — inner → outer. */
function collectVerticalScrollableAncestors(from: HTMLElement): HTMLElement[] {
  const out: HTMLElement[] = []
  let node: Element | null = from

  while (node) {
    if (node instanceof HTMLElement && node !== document.documentElement) {
      const st = window.getComputedStyle(node)
      const oy = st.overflowY
      const canScrollY =
        (oy === 'auto' || oy === 'scroll' || oy === 'overlay') &&
        node.scrollHeight > node.clientHeight + 1
      if (canScrollY) {
        out.push(node)
      }
    }
    const rn = node.getRootNode()
    if (rn instanceof ShadowRoot) {
      node = rn.host
    } else {
      node = node.parentElement
    }
  }

  return out
}

function centerElementVerticallyInContainer(
  elRect: DOMRect,
  container: HTMLElement,
): void {
  const cr = container.getBoundingClientRect()
  const relTop = elRect.top - cr.top + container.scrollTop
  const targetTop = relTop - container.clientHeight / 2 + elRect.height / 2
  const maxTop = Math.max(0, container.scrollHeight - container.clientHeight)
  container.scrollTo({
    top: Math.max(0, Math.min(targetTop, maxTop)),
    behavior: 'smooth',
  })
}

export function getThemePreviewShadowRoot(): ShadowRoot | null {
  const host = document.querySelector(
    '.document-html .document',
  ) as HTMLElement | null
  return host?.shadowRoot ?? null
}

/** Node `[data-comp]` đầu tiên khớp trong preview shadow. */
export function findPreviewDataCompElement(
  dataComp: string,
): HTMLElement | null {
  const shadowRoot = getThemePreviewShadowRoot()
  if (!shadowRoot || !dataComp) return null
  // So sánh attribute (không CSS.escape) — `data-comp` có dấu chấm (vd. Footer.options).
  const nodes = shadowRoot.querySelectorAll<HTMLElement>('[data-comp]')
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].getAttribute('data-comp') === dataComp) {
      return nodes[i]
    }
  }
  return null
}

/**
 * Đồng bộ class `is-editing` theo section đang mở (Navigate / open_option).
 * Preview click tự set class trong `handleClick` — effect này giữ sync khi đổi option.
 */
export function syncPreviewDataCompEditingClass(
  dataComp: string | null | undefined,
): void {
  const shadowRoot = getThemePreviewShadowRoot()
  if (!shadowRoot) return

  shadowRoot.querySelectorAll<HTMLElement>('[data-comp]').forEach(el => {
    const attr = el.getAttribute('data-comp')
    if (dataComp && attr === dataComp) {
      el.classList.add('is-editing')
    } else {
      el.classList.remove('is-editing')
    }
  })
}

/**
 * Preview HTML trong shadow DOM — bổ sung cuộn container ngoài (overflow) + host + element.
 */
export function scrollPreviewHtmlElementIntoView(element: HTMLElement): void {
  if (isPreviewHtmlScrollSuppressed()) return

  const chain = collectVerticalScrollableAncestors(element)
  const outermost = chain.length > 0 ? chain[chain.length - 1] : null

  if (outermost) {
    centerElementVerticallyInContainer(
      element.getBoundingClientRect(),
      outermost,
    )
  }

  const root = element.getRootNode()
  if (root instanceof ShadowRoot && root.host instanceof HTMLElement) {
    root.host.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    })
  }

  element.scrollIntoView(SCROLL_OPTS)
}

function clearNavigateScrollTimers(): void {
  navigateScrollTimers.forEach(id => window.clearTimeout(id))
  navigateScrollTimers.clear()
}

/** Hủy lịch cuộn Navigate đang chờ (đổi section nhanh / unmount). */
export function cancelScheduledNavigatePreviewScroll(): void {
  navigateScrollGeneration += 1
  clearNavigateScrollTimers()
}

/**
 * Cuộn preview tới `[data-comp]` khi user chọn section từ Navigate.
 * Có generation token + retry nhẹ — tránh race khi HTML/shadow chưa sẵn sàng.
 */
export function scheduleNavigatePreviewScroll(dataComp: string): void {
  if (!dataComp) return
  if (isPreviewHtmlScrollSuppressed()) return

  const generation = ++navigateScrollGeneration
  clearNavigateScrollTimers()

  const attempt = (n: number) => {
    if (generation !== navigateScrollGeneration) return
    if (isPreviewHtmlScrollSuppressed()) return

    const el = findPreviewDataCompElement(dataComp)
    if (el) {
      scrollPreviewHtmlElementIntoView(el)
      return
    }

    if (n >= NAVIGATE_SCROLL_MAX_ATTEMPTS) return
    const timerId = window.setTimeout(() => {
      navigateScrollTimers.delete(timerId)
      attempt(n + 1)
    }, NAVIGATE_SCROLL_RETRY_MS)
    navigateScrollTimers.add(timerId)
  }

  // Đợi 1 frame layout sau khi sidebar mở panel.
  requestAnimationFrame(() => {
    if (generation !== navigateScrollGeneration) return
    requestAnimationFrame(() => attempt(0))
  })
}
